<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'
import { $router } from '@/router'
import { $bus } from '@/common/events'
import { dialog } from '@/common/dialog'
import { toast } from '@/common/toast'
import { env } from '@/secrets/env'
import { useTitle } from '@/composables/title'
import { useRuntime } from '@/composables/runtime'
import { setZoomFactor } from '@/composables/electron'
import { useSettings } from '@/composables/settings'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useSessionTimer } from '@/composables/session'
import { useTheme } from '@/composables/theme.js'
import { useTranslate } from '@/composables/language.js'
import { useAuthState } from '@/composables/auth'
import { ConfigService } from '@/services/ConfigService.js'
import TabControl from '@/components/TabControl.vue'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import DropDown from '@/components/DropDown.vue'
import CheckBox from '@/components/CheckBox.vue'
import TextBox from '@/components/TextBox.vue'
import Button from '@/components/Button.vue'
import Label from '@/components/Label.vue'

const configService = new ConfigService();

const { uptime } = useRuntime();

const canChangeTheme = ref(false);
const originalThemeName = ref();
const time = ref("");

const isLoading = ref(true);

const { t, setLanguage, translateError } = useTranslate();

const languages = ref([
  { id: "en", name: "English" },
  { id: "si", name: "Slovenščina" },
]);

const dataSources = ref([
  //{ id: "local", name: "Lokalna baza" }, //TODO: Translate
  { id: "remote", name: t('settings.data_source_remote') },
]);

const tab = ref("general");

const tabs = ref([
  { id: "general", name: t('settings.tab_general') },
  { id: "network", name: t('settings.tab_network') },
  { id: "components", name: t('settings.tab_components') },
]);

const idleTimeouts = ref([
  { id: "10", name: t('settings.idle_10s') },
  { id: "15", name: t('settings.idle_15s') },
  { id: "30", name: t('settings.idle_30s') },
  { id: "60", name: t('settings.idle_60s') },
  { id: "120", name: t('settings.idle_120s') },
  { id: "300", name: t('settings.idle_300s') },
  { id: "600", name: t('settings.idle_600s') },
  { id: "900", name: t('settings.idle_900s') },
  { id: "1200", name: t('settings.idle_1200s') },
  { id: "1800", name: t('settings.idle_1800s') },
  { id: "3600", name: t('settings.idle_3600s') },
  { id: "7200", name: t('settings.idle_7200s') },
  { id: "14400", name: t('settings.idle_14400s') },
  { id: "28800", name: t('settings.idle_28800s') },
  { id: "86400", name: t('settings.idle_86400s') },
  { id: "-1", name: t('settings.idle_none') },
]);

const warningsBefore = ref([
  { id: "-1", name: t('settings.warning_none') },
  { id: "10", name: t('settings.warning_10s') },
  { id: "20", name: t('settings.warning_20s') },
  { id: "30", name: t('settings.warning_30s') },
  { id: "60", name: t('settings.warning_60s') },
]);

const wifiModes = ref([
  { id: "ap", name: t('settings.mode_access_point') },
  { id: "client", name: t('settings.mode_client') },
]);

const wifiSecurities = ref([
  //{ id: "none", name: "None" },
  //{ id: "wep", name: "WEP" },
  //{ id: "wpa", name: "WPA" },
  //{ id: "wpa2", name: "WPA2" },
  { id: "wpa2", name: "WPA/WPA2" },
]);

const themeOptions = ref([
  { id: "light", name: t('settings.theme_light') },
  { id: "dark", name: t('settings.theme_dark') },
]);

const traySelectOptions = ref([
  { id: "always-first", name: t('settings.tray_seq') },
  { id: "lowest-quantity-first", name: t('settings.tray_lowest_first') },
  { id: "highest-quantity-first", name: t('settings.tray_highest_first') },
]);

const autoReopenDelayOptions = ref([
  { id: "none", name: t('settings.auto_reopen_none') },
  { id: "200", name: t('settings.auto_reopen_200ms') },
  { id: "500", name: t('settings.auto_reopen_500ms') },
  { id: "1000", name: t('settings.auto_reopen_1000ms') },
  { id: "2000", name: t('settings.auto_reopen_2000ms') },
  { id: "5000", name: t('settings.auto_reopen_5000ms') },
]);

const catalogDisplayOptions = ref([
  { id: "list", name: t('settings.display_list') },
  { id: "grid", name: t('settings.display_grid') },
]);

const glovesScaleOptions = ref([
  { id: "100", name: "100%" },
  { id: "125", name: "125%" },
  { id: "150", name: "150%" },
]);

const scaleOptions = ref([
  { id: "0.50", name: "50%" },
  { id: "0.75", name: "75%" },
  { id: "1.00", name: "100%" },
  { id: "1.25", name: "125%" },
  { id: "1.50", name: "150%" },
  //{ id: "1.75", name: "175%" },
  //{ id: "2.00", name: "200%" },
  //{ id: "2.50", name: "250%" },
]);

const categoryDesignOptions = ref([
  { id: "1", name: "1" },
  { id: "2", name: "2" },
  { id: "3", name: "3" },
]);

const warningBeforeNone = ref("-1");

const scaleFactor = ref("100");

const cabinetControllers = ref([
  { model: "PLT-B414", ipv4: "10.8.0.1", port: 10001, mac: "aa:bb:cc:11:22:33" }
]);

const connectionOptions = ref([
  { id: "tcp", name: t('settings.connection_tcp') },
  { id: "serial", name: t('settings.connection_serial') },
]);

const barcodeScannerEnabled = ref();
const barcodeScannerModels = ref([
  { id: "generic", name: t('settings.barcode_generic') },
]);
const barcodeScannerModel = ref();
const barcodeScannerSerialPort = ref();
const barcodeScannerIPAddress = ref();
const barcodeScannerPort = ref();
const barcodeScannerConnection = ref();
/*
const barcodeScannerConnection = computed(() => {
  switch (barcodeScannerModel.value) {
    case "generic":
      return "serial";
  }
  return null;
});
*/

const cardReaderEnabled = ref();
const cardReaderModels = ref([
  { id: "jantar", name: "Jantar REX-X-1-A" },
  { id: "wiegand", name: "Wiegand" },
]);
const cardReaderModel = ref();
const cardReaderSerialPort = ref();
const cardReaderIPAddress = ref();
const cardReaderPort = ref();
const cardReaderConnection = ref();
/*
const cardReaderConnection = computed(() => {
  switch (cardReaderModel.value) {
    case "jantar":
      return "tcp";

    case "wiegand":
      return "serial";
  }
  return null;
});
*/

const weightScaleEnabled = ref(false);
const weightScaleModels = ref([
  { id: "kern", name: "Kern CPE 15K3" },
  { id: "ohaus", name: "Ohaus RC31P30" },
  { id: "plt-ws1", name: "PLT-WS1" },
  { id: "plt-ws2", name: "PLT-WS2" },
]);
const weightScaleModel = ref("plt-ws2");
const weightScaleSerialPort = ref("none");

const availableSerialPorts = ref([
  { id: "none", name: "None" },
]);

const error = ref();

const settings = ref({
  version: "0.16.0", //Application version //TODO: Read from build env. var.
  language: "si", //Default language
  dataSource: "local", //Data source
  session: { //User's login session
    idleTimeout: "900", //Max session duration
    warningBefore: "30", //Warn N seconds before the session expires
    alertTimeout: null, //Max session duration
    promptForLogout: false,
    trayAutoLogout: false,
  },
  display: { //Display management
    dpmsTimeout: -1, //Screen blanking turned off
    zoomFactor: 1.0, //Performed on window, default 1.0 = 100%
    scale: 100
  },
  theme: { //Theme settings
    name: "light",
  },
  remote: { //Remote access
    enabled: false,
  },
  virtualKeyboard: false,
  ethernet: { //Cable network
    enabled: true,
    dhcp: false, //dhcp, static
    ipv4: "192.168.1.24",
    netmask: "255.255.255.0",
    gateway: "192.168.1.1",
    dnsServers: "8.8.8.8, 8.8.4.4",
  },
  wifi: { //Wireless network
    enabled: true,
    mode: "ap", //client or access point
    dhcp: false, //dhcp, static
    ipv4: "192.168.1.25",
    netmask: "255.255.255.0",
    gateway: "192.168.1.1",
    dnsServers: "8.8.8.8, 8.8.4.4",
    ssid: "Omara414",
    password: "omara414",
    security: "wpa2"
  },
  mobile: { //Mobile data
    enabled: false,
    ipv4: "192.168.1.24",
    netmask: "255.255.255.0",
    gateway: "192.168.1.1",
    dnsServers: "8.8.8.8, 8.8.4.4",
  },
  vpn: { //WireGuard VPN connection
    enabled: false,
    ipv4: "192.168.1.24",
    netmask: "255.255.255.0",
    gateway: "192.168.1.1",
  },
  catalog: { //Catalog display settings
    articleListMode: "current-cabinet", //current-cabinet, all-cabinets
    articleVisibility: "always", //always, last category
    categoryVisibility: "always", //always, never
    parameterVisibility: "always", //always, last category, never
    canSendEmptyStockEmail: false,
    traySelectMode: null,
  },
  usage: {
    fastFilling: false,
    canReportError: false,
  },
  preferences: {
    listUsersItems: false,
    autoReopenDelay: "500",
    localCache: "disabled",
  }
});

//Auxiliary settings - to be mapped with "settings" on load/save
const aux = ref({
  isArticleListForAllCabinets: settings.value.catalog?.articleListMode == "all-cabinets",
  isArticleVisibleOnlyOnLastCategory: settings.value.catalog?.articleVisibility == "last category",
  isCategoryVisible: settings.value.catalog?.categoryVisibility == "always",
  isParameterVisible: settings.value.catalog?.parameterVisibility == "always",
  isCompleteCatalogVisible: settings.value.catalog?.catalogDataVisibility == "all",
  catalogDisplayMode: settings.value.catalog?.displayMode,
  traySelectMode: settings.value.catalog?.traySelectMode,
  autoReopenDelay: settings.value.preferences?.autoReopenDelay,
  canSendEmptyStockEmail: settings.value.catalog?.canSendEmptyStockEmail,
  promptForLogout: settings.value.session?.promptForLogout,
  trayAutoLogout: settings.value.session?.isTrayAutoLogout,
  isWeightMeasureMandatory: settings.value.usage?.weightMeasure == "required", //required, optional
  fastFilling: settings.value.usage?.fastFilling,
  canReportError: settings.value.usage?.canReportError,
  listUsersItems: settings.value.preferences?.listUsersItems,
  isLocalCacheEnabled: settings.value.preferences?.localCache == "enabled",
  isDebugVirtualKeyboardCaret: settings.value.debug?.vkcaret,
  categoryDesign: settings.value.debug?.categoryDesign || 1,
  isDebugCatalog2: settings.value.debug?.catalog2,
  isDebugEnabled: settings.value.debug?.enabled
});

const status = ref({
  ethernet: "none", //status: "connected"
  mobile: "none",
  wifi: "none", //connected, disconnected (idle), not available (no hw found), initializing, searching (for ssid), ready (access point ready to accept connections)
  vpn: "none",
});

function updateTime() {
  let duration = uptime();

  let s = Math.floor(duration / 1000 % 60);
  let m = Math.floor(duration / 1000 / 60 % 60);
  let h = Math.floor(duration / 1000 / 3600);

  let hh = (h < 10 ? "0" : "") + h;
  let mm = (m < 10 ? "0" : "") + m;
  let ss = (s < 10 ? "0" : "") + s;

  time.value = `${hh}:${mm}:${ss}`;
}

setInterval(updateTime, 1000);
updateTime();

const useCase = ref();
const allowNetworkSettings = ref(false);
const allowArticleSettings = ref(false);
const allowGloveSettings = ref(false);

onMounted(async () => {
  useTitle(t('menu.settings'));
  useMenu("settings");
  useSubmenu(false);
  useTopBar(true);

  useSessionTimer(false);

  initPermissions();

  try {
    await loadSettings();
  }
  catch (err) {
    console.error("[settings] Cannot load settings", err);
    err = translateError(err);
    error.value = err.message;
  }

  try {
    await loadSerialPorts();
  }
  catch (err) {
    console.error("[settings] Cannot load serial ports", err);
    err = translateError(err);
    if (!err.isSyntaxError) { //Ignore API error, already displayed once in previous try-catch block
      toast(err);
    }
  }

  isLoading.value = false;
});

//Validate MAC address, e.g. aa:bb:cc:11:22:33
function mac(value) {
  if (!value) {
    return;
  }
  if (!/^([0-9a-f]{2}:[0-9a-f]{2}:[0-9a-f]{2}:[0-9a-f]{2}:[0-9a-f]{2}:[0-9a-f]{2})$/gi.test(value)) {
    return t('settings.error_mac_invalid');
  }
}

//Validate port number, must be 0 < port < 65536
function port(value) {
  if (!value) {
    return;
  }
  let inRange = parseInt(value) > 0 && parseInt(value) < 65536;
  if (!inRange) {
    return t('settings.error_port_range');
  }
}

//Validate IPv4 address, e.g. 192.168.4.1
function ip(addr) {
  if (!addr) {
    return;
  }
  if (!/^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/.test(addr)) {
    return t('settings.error_ip_invalid');
  }
}

//Validate IPv4 list, e.g. 8.8.8.8, 8.8.4.4
function iplist(list) {
  if (!list) {
    return t('settings.error_ip_list_missing');
  }
  let parts = list.split(/,/g);
  for (let part of parts) {
    let msg = ip(part.trim());
    if (msg) {
      return msg;
    }
  }
}

function nonEmpty(value) {
  if (!value || value.trim().length == 0) {
    return "Missing value";
  }
}

function minEightChars(pwd) {
  if (!pwd || pwd.length < 8) {
    return "Expected at least 8 chars";
  }
}

function uppercase(value) {
  if (!value) {
    return;
  }
  return value.toUpperCase();
}

function netmask(value) {
  if (!value) {
    return;
  }
  if (value == 32) {
    return "255.255.255.255";
  }
  if (value == 24) {
    return "255.255.255.0";
  }
  if (value == 16) {
    return "255.255.0.0";
  }
  if (value == 8) {
    return "255.0.0.0";
  }
  if (value == 0) {
    return "0.0.0.0";
  }
}

async function loadSerialPorts() {
  let ports = await configService.getSerialPorts();
  let options = (ports || [ ]).map(x => ({ id: x.name, name: x.name }));
  let serialPorts = [
    { id: "none", name: "None" }, //TODO: Translate
    ...options
  ]
  availableSerialPorts.value = serialPorts;
}

async function loadSettings() {
  let config = await configService.getSettings({ network: true });

  originalThemeName.value = config.theme?.name || localStorage.getItem("theme");
  canChangeTheme.value = true;
  
  //Apply changes to auxiliary settings
  aux.value.isArticleListForAllCabinets = config.catalog?.articleListMode == "all-cabinets";
  aux.value.isArticleVisibleOnlyOnLastCategory = config.catalog?.articleVisibility == "last category";
  aux.value.isCategoryVisible = config.catalog?.categoryVisibility == "always";
  aux.value.isParameterVisible = config.catalog?.parameterVisibility == "always";
  aux.value.isCompleteCatalogVisible = config.catalog?.catalogDataVisibility == "all";
  aux.value.canSendEmptyStockEmail = config.catalog?.canSendEmptyStockEmail;
  aux.value.catalogDisplayMode = config.catalog?.displayMode;
  aux.value.traySelectMode = config.catalog?.traySelectMode;
  aux.value.autoReopenDelay = config.preferences?.autoReopenDelay;
  aux.value.isTrayAutoLogout = config.session?.trayAutoLogout;
  aux.value.promptForLogout = config.session?.promptForLogout;
  aux.value.isWeightMeasureMandatory = config.usage?.weightMeasure == "required"; //required, optional
  aux.value.fastFilling = config.usage?.fastFilling;
  aux.value.canReportError = config.usage?.canReportError;
  aux.value.listUsersItems = config.preferences?.listUsersItems;
  aux.value.isLocalCacheEnabled = config.preferences?.localCache == "enabled";
  aux.value.isDebugVirtualKeyboardCaret = config.debug?.vkcaret;
  aux.value.categoryDesign = config.debug?.categoryDesign || 1;
  aux.value.isDebugCatalog2 = config.debug?.catalog2;
  aux.value.isDebugEnabled = config.debug?.enabled;

  scaleFactor.value = config.display?.scale || "100";

  if (!config.theme) {
    config.theme = { name: "light" };
  }

  if (!config.ethernet) {
    config.ethernet = { };
  }

  if (config.cabinets) {
    let list = [ ];
    for (let cabinet of config.cabinets) {
      list.push({
        enabled: cabinet.enabled,
        name: cabinet.name,
        model: cabinet.model,
        modelName: cabinet.model?.toUpperCase() || cabinet.name,
        mac: cabinet.mac,
        ipv4: cabinet.host,
        port: cabinet.port,
      });
    }
    cabinetControllers.value = list;
  }

  if (config.barcode) {
    barcodeScannerEnabled.value = config.barcode.enabled;
    barcodeScannerModel.value = config.barcode.model;
    if (config.barcode.use == "tcp") {
      barcodeScannerIPAddress.value = config.barcode.host;
      barcodeScannerPort.value = config.barcode.port;
      barcodeScannerConnection.value = "tcp";
    }
    else if (config.barcode.use == "serial") {
      barcodeScannerSerialPort.value = config.barcode.port;
      barcodeScannerConnection.value = "serial";
    }
  }

  if (config.smartcard) {
    cardReaderEnabled.value = config.smartcard.enabled;
    cardReaderModel.value = config.smartcard.model;
    if (config.smartcard.use == "tcp") {
      cardReaderIPAddress.value = config.smartcard.host;
      cardReaderPort.value = config.smartcard.port;
      cardReaderConnection.value = "tcp";
    }
    else if (config.smartcard.use == "serial") {
      cardReaderSerialPort.value = config.smartcard.port;
      cardReaderConnection.value = "serial";
    }
  }

  if (config.weightScale) {
    weightScaleEnabled.value = config.weightScale.enabled;
    weightScaleModel.value = config.weightScale.model;
    weightScaleSerialPort.value = config.weightScale.port;
  }

  if (config.network) {
    for (let network of config.network) {
      if (network.type == "vpn") {
        config.vpn = {
          enabled: network.state == "up",
          mac: network.mac,
          ipv4: network.ipv4,
          netmask: netmask(network.cidr),
          gateway: null,
        };
      }
      if (network.type == "mobile") {
        config.mobile = {
          enabled: network.state == "up",
          mac: network.mac,
          ipv4: network.ipv4,
          netmask: netmask(network.cidr),
          gateway: null,
        };
      }
    }
  }

  settings.value = config;

  //Override values
  if (settings.value.remote) {
    settings.value.remote.enabled = true;
  }
}

function initPermissions() {
  useCase.value = env.VITE_USE_CASE;
  if (useCase.value == "gloves") {
    allowNetworkSettings.value = false;
    allowArticleSettings.value = false;
    allowGloveSettings.value = true;
  }
  else {
    allowNetworkSettings.value = true;
    allowArticleSettings.value = true;
    allowGloveSettings.value = false;
  }
}

async function saveSettings() {
  try {
    let config = settings.value;

    //Apply changes from auxiliary settings
    if (!config.catalog) {
      config.catalog = { };
    }
    config.catalog.articleListMode = aux.value.isArticleListForAllCabinets ? "all-cabinets" : "current-cabinet";
    config.catalog.articleVisibility = aux.value.isArticleVisibleOnlyOnLastCategory ? "last category" : "always";
    config.catalog.categoryVisibility = aux.value.isCategoryVisible ? "always" : "never";
    config.catalog.parameterVisibility = aux.value.isParameterVisible ? "always" : "never";
    config.catalog.catalogDataVisibility = aux.value.isCompleteCatalogVisible ? "all" : "filtered"; //Display either complete catalogue or filtered (only those articles defined for this cabinet + satellites)
    config.catalog.displayMode = aux.value.catalogDisplayMode;
    config.catalog.traySelectMode = aux.value.traySelectMode;
    config.catalog.canSendEmptyStockEmail = aux.value.canSendEmptyStockEmail;

    //User session
    if (!config.session) {
      config.session = { };
    }
    config.session.promptForLogout = aux.value.promptForLogout;
    config.session.trayAutoLogout = aux.value.isTrayAutoLogout;

    //Usage cases
    if (!config.usage) {
      config.usage = { };
    }
    config.usage.weightMeasure = aux.value.isWeightMeasureMandatory ? "required" : "optional"; //required, optional
    config.usage.fastFilling = aux.value.fastFilling;
    config.usage.canReportError = aux.value.canReportError;

    //Preferences
    if (!config.preferences) {
      config.preferences = { };
    }
    config.preferences.listUsersItems = aux.value.listUsersItems;
    config.preferences.autoReopenDelay = aux.value.autoReopenDelay;
    config.preferences.localCache = aux.value.isLocalCacheEnabled ? "enabled" : "disabled";

    //Debug settings
    if (!config.debug) {
      config.debug = { };
    }
    config.debug.enabled = aux.value.isDebugEnabled;
    config.debug.catalog2 = aux.value.isDebugCatalog2;
    config.debug.vkcaret = aux.value.isDebugVirtualKeyboardCaret;
    config.debug.categoryDesign = aux.value.categoryDesign;

    //Display settings
    if (!config.display) {
      config.display = { };
    }
    config.display.scale = scaleFactor.value; //Gloves only, to be removed in future updates
    //config.display.zoomFactor = settings.value?.display?.zoomFactor; //New approach for zooming
    if (config.display.zoomFactor) {
      setZoomFactor(config.display.zoomFactor); //Apply browser zoom factor
    }

    if (allowNetworkSettings.value) {
      delete config.ethernet;
      delete config.wifi;
      delete config.mobile;
    }

    //Apply language
    setLanguage(settings.value.language);

    //Save configuration to database
    await configService.setSettings(config);
    
    //Apply theme
    originalThemeName.value = config.theme?.name;
    canChangeTheme.value = false;

    //Apply changes immediately in this runtime
    useSettings(config, { replace: true });

    //Note that setting config may reset the timer. Therefore turn off the session timer after setup.
    setTimeout(() => useSessionTimer(false), 200);

    //Redirect
    let catalogRoute = aux.value.isDebugCatalog2 ? "Catalog2" : "Catalog";
    $router.push({ name: catalogRoute });
  }
  catch (err) {
    dialog(err);
  }
}

async function cancel() {
  try {
    //Restore back the original theme
    useTheme(originalThemeName.value);

    //TODO: Detect changes and save?
    let catalogRoute = aux.value.isDebugCatalog2 ? "Catalog2" : "Catalog";
    $router.push({ name: catalogRoute });
  }
  catch (err) {
    dialog(err);
  }
}

//Set zoom level
function zoomFactorChanged(e) {
  //console.log("zoomFactorChanged", settings.value?.display?.zoomFactor, e);
  // if (settings.value?.display?.zoomFactor) {
  //   setZoomFactor(settings.value.display.zoomFactor);
  // }
}

function themeChanged(e) {
  //Only when settings are loaded
  if (canChangeTheme.value) {
    let themeName = settings.value?.theme?.name;
    useTheme(themeName);
  }
}

//Ask to save before leaving the page
onBeforeRouteLeave(async (to, from, next) => {
  //await askSaveChanges();
  useTheme(originalThemeName.value); //Restore to original or saved
  next();
});

function onLanguageChanged(e) {
  //Do NOT change language before saved
  //setLanguage(settings.value.language);
}
</script>

<template>
  <div class="screen-container">

    <!-- Error message -->
    <div v-if="error">
      <pre class="p-5 mt-4 mb-10 font-sans border rounded-md bg-red-100 text-red-700 text-sm dark:bg-red-800 dark:border-red-900 dark:text-white"><i class="fa fa-xmark-circle mr-4"></i>{{ error.message }}</pre>
    </div>

    <!-- Loading animation -->
    <div class="grid place-items-center w-full h-[calc(100vh-120px)] " v-else-if="isLoading && !error">
      <Spinner size="12"></Spinner>
    </div>

    <!-- Content -->
    <div v-else>
    
      <div class="flex justify-between">
        <h2 class="text-lg font-bold dark:text-white">{{ $t('menu.settings') }}</h2>
        <div class="flex flex-row gap-4">
          <Button type="secondary" @click="cancel">{{$t('generic.cancel')}}</Button>
          <Button type="primary" @click="saveSettings">{{$t('generic.save')}}</Button>
        </div>
      </div>

      <TabControl v-model="tab" :items="tabs" class="mb-4"></TabControl>

      <form autocomplete="off">
        <div class="grid sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"><!-- lg:flex lg:flex-row -->

          <!-- General settings -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'general'"><!-- w-64 mt-6 mb-6 -->
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.general_settings')}}</span>
            </h6>
            <div>
              <Label>{{$t('settings.label_data_source')}}</Label>
              <DropDown :placeholder="$t('settings.select_data_source')" :options="dataSources" v-model="settings.dataSource" :enabled="false" />
            </div>
            <div>
              <Label>{{$t('settings.label_default_language')}}</Label>
              <DropDown :placeholder="$t('settings.select_language')" :options="languages" v-model="settings.language" :enabled="true" @change="onLanguageChanged" />
            </div>
            <div v-if="settings.theme">
              <Label>{{$t('settings.label_theme')}}</Label>
              <DropDown :placeholder="$t('settings.select_color_scheme')" :options="themeOptions" v-model="settings.theme.name" :enabled="true" @change="themeChanged" />
            </div>
            <div v-if="settings.display">
              <Label>{{$t('settings.label_dpms_timeout')}}</Label>
              <DropDown :placeholder="$t('settings.select_item')" :options="idleTimeouts" v-model="settings.display.dpmsTimeout" />
            </div>
            <div v-if="settings.display">
              <Label>{{$t('settings.label_zoom_level')}}</Label>
              <DropDown :placeholder="$t('settings.select_zoom')" :options="scaleOptions" v-model="settings.display.zoomFactor" :enabled="true" @change="zoomFactorChanged" />
            </div>
            <div v-if="allowGloveSettings">
              <Label>{{$t('settings.label_zoom_level')}} (2)</Label>
              <DropDown :placeholder="$t('settings.select_zoom')" :options="glovesScaleOptions" v-model="scaleFactor" :enabled="true" />
            </div>
            <div>
              <Label>Category design</Label>
              <DropDown :placeholder="$t('settings.select_zoom')" :options="categoryDesignOptions" v-model="aux.categoryDesign" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="settings.remote.enabled" :label="$t('settings.enable_remote_access')" :enabled="false" />
            </div>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="aux.isLocalCacheEnabled" :label="$t('settings.enable_local_cache')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="settings.virtualKeyboard" :label="$t('settings.enable_virtual_keyboard')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="aux.isDebugVirtualKeyboardCaret" :label="$t('settings.virtualKeyboardCaret')" :enabled="true" />
            </div>
            <!-- <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="aux.isDebugEnabled" :label="$t('settings.enableDebug')" :enabled="true" />
            </div> -->
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="aux.isDebugCatalog2" :label="$t('settings.enableDebugCatalog2')" :enabled="true" />
            </div>
          </div>

          <!-- Cabinet usage settings -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'general'">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.usageSettings')}}</span>
            </h6>
            <div>
              <Label>{{$t('settings.label_idle_timeout')}}</Label>
              <DropDown :placeholder="$t('settings.select_item')" :options="idleTimeouts" v-model="settings.session.idleTimeout" />
            </div>
            <div>
              <Label :enabled="settings.session.idleTimeout > 60">{{$t('settings.label_warning_before')}}</Label>
              <DropDown :placeholder="$t('settings.select_item')" :options="warningsBefore" v-model="settings.session.warningBefore" :enabled="true" v-if="settings.session.idleTimeout > 60" />
              <DropDown :placeholder="$t('settings.select_item')" :options="warningsBefore" v-model="warningBeforeNone" :enabled="false" v-else />
            </div>
            <div>
              <Label>{{$t('settings.label_alert_timeout')}}</Label>
              <DropDown :placeholder="$t('settings.select_item')" :options="idleTimeouts" v-model="settings.session.alertTimeout" />
            </div>
            <div>
              <Label>{{ $t('settings.article_display') }}</Label>
              <DropDown :placeholder="$t('settings.select_item')" :options="catalogDisplayOptions" v-model="aux.catalogDisplayMode" :enabled="true" />
            </div>
            <div>
              <Label>{{ $t('settings.tray_select') }}</Label>
              <DropDown :placeholder="$t('settings.select_item')" :options="traySelectOptions" v-model="aux.traySelectMode" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2" v-if="allowArticleSettings">
              <CheckBox v-model="aux.isArticleVisibleOnlyOnLastCategory" :label="$t('settings.show_articles_last_category')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2" v-if="allowArticleSettings">
              <CheckBox v-model="aux.isArticleListForAllCabinets" :label="$t('settings.show_articles_all_cabinets')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2" v-if="allowArticleSettings">
              <CheckBox v-model="aux.isCategoryVisible" :label="$t('settings.show_categories')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2" v-if="allowArticleSettings">
              <CheckBox v-model="aux.isParameterVisible" :label="$t('settings.show_parameters')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2" v-if="allowArticleSettings">
              <CheckBox v-model="aux.isCompleteCatalogVisible" :label="$t('settings.show_all_catalog')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2" v-if="allowArticleSettings">
              <CheckBox v-model="aux.listUsersItems" :label="$t('settings.show_users_items')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2" v-if="allowGloveSettings">
              <CheckBox v-model="aux.canReportError" :label="$t('settings.show_error_report')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2" v-if="allowGloveSettings">
              <CheckBox v-model="aux.promptForLogout" :label="$t('settings.ask_logout')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2" v-if="allowGloveSettings">
              <CheckBox v-model="aux.fastFilling" :label="$t('settings.fast_filling')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="aux.canSendEmptyStockEmail" :label="$t('settings.send_empty_stock_email')" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="aux.isTrayAutoLogout" :label="$t('settings.auto_logout_after_tray')" :enabled="true" />
            </div>
          </div>

          <!-- Weight scale settings -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'general'">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.scaleSettings')}}</span>
            </h6>
            <div>
              <Label>{{ $t('settings.auto_reopen_delay') }}</Label>
              <DropDown :placeholder="$t('settings.select_item')" :options="autoReopenDelayOptions" v-model="aux.autoReopenDelay" :enabled="true" />
            </div>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="aux.isWeightMeasureMandatory" :label="$t('settings.weight_mandatory')" :enabled="true" />
            </div>
          </div>

          <!-- Ethernet settings -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'network' && allowNetworkSettings && settings.ethernet">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.network_settings')}}</span>
              <i class="fa fa-regular fa-lightbulb text-yellow-300 mt-1 animate-bounce" v-if="status.ethernet == 'pending'"></i>
              <i class="fa fa-solid fa-lightbulb text-red-600 mt-1" v-else-if="status.ethernet == 'disconnected'"></i>
              <i class="fa fa-solid fa-lightbulb text-green-400 mt-1" v-else-if="status.ethernet == 'connected'"></i>
              <i class="fa fa-regular fa-lightbulb text-slate-400 mt-1" v-else-if="status.ethernet == 'neutral'"></i>
            </h6>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="settings.ethernet.dhcp" :label="$t('settings.enable_dhcp')" />
            </div>
            <div>
              <Label :enabled="!settings.ethernet.dhcp">{{$t('settings.label_ip_address')}}</Label>
              <TextBox placeholder="192.168.1.10" v-model="settings.ethernet.ipv4" :enabled="!settings.ethernet.dhcp" :validation="ip(settings.ethernet.ipv4)" class="w-full" />
            </div>
            <div>
              <Label :enabled="!settings.ethernet.dhcp">{{$t('settings.label_netmask')}}</Label>
              <TextBox placeholder="255.255.255.0" v-model="settings.ethernet.netmask" :enabled="!settings.ethernet.dhcp" :validation="ip(settings.ethernet.netmask)" class="w-full" />
            </div>
            <div>
              <Label :enabled="!settings.ethernet.dhcp">{{$t('settings.label_gateway')}}</Label>
              <TextBox placeholder="192.168.1.1" v-model="settings.ethernet.gateway" :enabled="!settings.ethernet.dhcp" :validation="ip(settings.ethernet.gateway)" class="w-full" />
            </div>
            <div>
              <Label :enabled="!settings.ethernet.dhcp">{{$t('settings.label_dns_servers')}}</Label>
              <TextBox placeholder="8.8.8.8, 8.8.4.4" v-model="settings.ethernet.dnsServers" :enabled="!settings.ethernet.dhcp" :validation="iplist(settings.ethernet.dnsServers)" class="w-full" />
            </div>
          </div>

          <!-- WireGuard settings -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'network' && allowNetworkSettings && settings.vpn">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.vpnSettings')}}</span>
              <i class="fa fa-regular fa-lightbulb text-yellow-300 mt-1 animate-bounce" v-if="status.vpn == 'pending'"></i>
              <i class="fa fa-solid fa-lightbulb text-red-600 mt-1" v-else-if="status.vpn == 'disconnected'"></i>
              <i class="fa fa-solid fa-lightbulb text-green-400 mt-1" v-else-if="status.vpn == 'connected'"></i>
              <i class="fa fa-regular fa-lightbulb text-slate-400 mt-1" v-else-if="status.vpn == 'neutral'"></i>
            </h6>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="settings.vpn.enabled" :enabled="false" :label="$t('settings.enableVPN')" />
            </div>
            <div>
              <Label :enabled="settings.vpn.enabled">{{$t('settings.label_ip_address')}}</Label>
              <TextBox placeholder="" v-model="settings.vpn.ipv4" :enabled="false" :validation="ip(settings.vpn.ipv4)" class="w-full" />
            </div>
            <div>
              <Label :enabled="settings.vpn.enabled">{{$t('settings.label_netmask')}}</Label>
              <TextBox placeholder="" v-model="settings.vpn.netmask" :enabled="false" :validation="ip(settings.vpn.netmask)" class="w-full" />
            </div>
            <div>
              <Label :enabled="settings.vpn.enabled">{{$t('settings.label_gateway')}}</Label>
              <TextBox placeholder="" v-model="settings.vpn.gateway" :enabled="false" :validation="ip(settings.vpn.gateway)" class="w-full" />
            </div>
          </div>

          <!-- WiFi settings -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'network' && allowNetworkSettings && settings.wifi">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.wifi_settings')}}</span>
              <i class="fa fa-regular fa-lightbulb text-yellow-300 mt-1 animate-bounce" v-if="status.wifi == 'pending'"></i>
              <i class="fa fa-solid fa-lightbulb text-red-600 mt-1" v-else-if="status.wifi == 'disconnected'"></i>
              <i class="fa fa-solid fa-lightbulb text-green-400 mt-1" v-else-if="status.wifi == 'connected'"></i>
              <i class="fa fa-regular fa-lightbulb text-slate-400 mt-1" v-else-if="status.ethernet == 'neutral'"></i>
            </h6>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="settings.wifi.enabled" :enabled="false" :label="$t('settings.enable_wifi')" />
            </div>
            <div>
              <Label>{{$t('settings.label_mode')}}</Label>
              <DropDown :placeholder="$t('settings.select_mode')" :options="wifiModes" v-model="settings.wifi.mode" />
            </div>
            <div>
              <Label :enabled="false">{{$t('settings.label_security')}}</Label>
              <DropDown :placeholder="$t('settings.select_wpa')" :options="wifiSecurities" v-model="settings.wifi.security" :enabled="wifiSecurities.length > 1" />
            </div>
            <div>
              <Label>{{$t('settings.label_ssid')}}</Label>
              <TextBox :placeholder="$t('settings.placeholder_ssid')" v-model="settings.wifi.ssid" :validation="nonEmpty(settings.wifi.ssid)" class="w-full" />
            </div>
            <div>
              <Label>{{$t('settings.label_password')}}</Label>
              <TextBox :placeholder="$t('settings.placeholder_password')" type="password" v-model="settings.wifi.password" :validation="minEightChars(settings.wifi.password)" class="w-full" />
            </div>
          </div>

          <!-- WiFi advanced -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'network' && allowNetworkSettings && settings.wifi">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.wifi_settings_advanced')}}</span>
            </h6>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="settings.wifi.dhcp" :label="$t('settings.enable_dhcp')" :enabled="settings.wifi.mode != 'ap'" />
            </div>
            <div>
              <Label :enabled="!settings.wifi.dhcp && settings.wifi.mode != 'ap'">{{$t('settings.label_ip_address')}}</Label>
              <TextBox placeholder="" v-model="settings.wifi.ipv4" :enabled="!settings.wifi.dhcp && settings.wifi.mode != 'ap'" :validation="ip(settings.wifi.ipv4)" class="w-full" />
            </div>
            <div>
              <Label :enabled="!settings.wifi.dhcp && settings.wifi.mode != 'ap'">{{$t('settings.label_netmask')}}</Label>
              <TextBox placeholder="" v-model="settings.wifi.netmask" :enabled="!settings.wifi.dhcp && settings.wifi.mode != 'ap'" :validation="ip(settings.wifi.netmask)" class="w-full" />
            </div>
            <div>
              <Label :enabled="!settings.wifi.dhcp && settings.wifi.mode != 'ap'">{{$t('settings.label_gateway')}}</Label>
              <TextBox placeholder="" v-model="settings.wifi.gateway" :enabled="!settings.wifi.dhcp && settings.wifi.mode != 'ap'" :validation="ip(settings.wifi.gateway)" class="w-full" />
            </div>
            <div>
              <Label :enabled="!settings.wifi.dhcp && settings.wifi.mode != 'ap'">{{$t('settings.label_dns_servers')}}</Label>
              <TextBox placeholder="" v-model="settings.wifi.dnsServers" :enabled="!settings.wifi.dhcp && settings.wifi.mode != 'ap'" :validation="iplist(settings.wifi.dnsServers)" class="w-full" />
            </div>
          </div>

          <!-- Mobile data settings -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'network' && allowNetworkSettings && settings.mobile">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.mobileSettings')}}</span>
              <i class="fa fa-regular fa-lightbulb text-yellow-300 mt-1 animate-bounce" v-if="status.mobile == 'pending'"></i>
              <i class="fa fa-solid fa-lightbulb text-red-600 mt-1" v-else-if="status.mobile == 'disconnected'"></i>
              <i class="fa fa-solid fa-lightbulb text-green-400 mt-1" v-else-if="status.mobile == 'connected'"></i>
              <i class="fa fa-regular fa-lightbulb text-slate-400 mt-1" v-else-if="status.mobile == 'neutral'"></i>
            </h6>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="settings.mobile.enabled" :enabled="false" :label="$t('settings.enableMobileData')" />
            </div>
            <div>
              <Label :enabled="settings.mobile.enabled">{{$t('settings.label_ip_address')}}</Label>
              <TextBox placeholder="" v-model="settings.mobile.ipv4" :enabled="false" :validation="ip(settings.mobile.ipv4)" class="w-full" />
            </div>
            <div>
              <Label :enabled="settings.mobile.enabled">{{$t('settings.label_netmask')}}</Label>
              <TextBox placeholder="" v-model="settings.mobile.netmask" :enabled="false" :validation="ip(settings.mobile.netmask)" class="w-full" />
            </div>
            <div>
              <Label :enabled="settings.mobile.enabled">{{$t('settings.label_gateway')}}</Label>
              <TextBox placeholder="" v-model="settings.mobile.gateway" :enabled="false" :validation="ip(settings.mobile.gateway)" class="w-full" />
            </div>
          </div>

          <!-- Components: cabinet controller -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'components'" v-for="(cabinetController, index) in cabinetControllers">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.controllerSettings')}} {{ index + 1 }}</span>
            </h6>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="cabinetController.enabled" :enabled="false" :label="$t('settings.enable')" />
            </div>
            <div>
              <Label :enabled="cabinetController.enabled">{{ $t('settings.controller_model') }}</Label>
              <TextBox placeholder="--" v-model="cabinetController.modelName" :enabled="false" class="w-full" />
            </div>
            <div>
              <Label :enabled="cabinetController.enabled">{{ $t('settings.mac_address') }}</Label>
              <TextBox placeholder="--" v-model="cabinetController.mac" :enabled="false" :validation="mac(cabinetController.mac)" class="w-full" />
            </div>
            <div>
              <Label :enabled="cabinetController.enabled">{{ $t('settings.ip_address') }}</Label>
              <TextBox placeholder="--" v-model="cabinetController.ipv4" :enabled="false" :validation="ip(cabinetController.ipv4)" class="w-full" />
            </div>
            <div>
              <Label :enabled="cabinetController.enabled">{{ $t('settings.port') }}</Label>
              <TextBox placeholder="--" v-model="cabinetController.port" :enabled="false" :validation="port(cabinetController.port)" class="w-full" />
            </div>
          </div>

          <!-- Components: barcode scanner -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'components'">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.barcodeSettings')}}</span>
            </h6>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="barcodeScannerEnabled" :enabled="false" :label="$t('settings.enable')" />
            </div>
            <div>
              <Label :enabled="barcodeScannerEnabled">{{ $t('settings.select_model') }}</Label>
              <DropDown :placeholder="$t('settings.select_model')" :options="barcodeScannerModels" v-model="barcodeScannerModel" :enabled="false" />
            </div>
            <div>
              <Label :enabled="barcodeScannerEnabled">{{ $t('settings.select_connection') }}</Label>
              <DropDown :placeholder="$t('settings.select_connection')" :options="connectionOptions" v-model="barcodeScannerConnection" :enabled="false" />
            </div>
            <div v-if="barcodeScannerConnection == 'serial'">
              <Label :enabled="barcodeScannerEnabled">{{ $t('settings.serial_port') }}</Label>
              <DropDown :placeholder="$t('settings.select_port')" :options="availableSerialPorts" v-model="barcodeScannerSerialPort" :enabled="false" />
            </div>
            <div v-if="barcodeScannerConnection == 'tcp'">
              <Label :enabled="barcodeScannerEnabled">{{ $t('settings.ip_address') }}</Label>
              <TextBox placeholder="" v-model="barcodeScannerIPAddress" :enabled="false" :validation="ip(barcodeScannerIPAddress)" class="w-full" />
            </div>
            <div v-if="barcodeScannerConnection == 'tcp'">
              <Label :enabled="barcodeScannerEnabled">{{ $t('settings.port') }}</Label>
              <TextBox placeholder="" v-model="barcodeScannerPort" :enabled="false" :validation="port(barcodeScannerPort)" class="w-full" />
            </div>
          </div>

          <!-- Components: RFID125 card reader -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'components'">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.cardReaderSettings')}}</span>
            </h6>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="cardReaderEnabled" :enabled="false" :label="$t('settings.enable')" />
            </div>
            <div>
              <Label :enabled="cardReaderEnabled">{{ $t('settings.select_model') }}</Label>
              <DropDown :placeholder="$t('settings.select_model')" :options="cardReaderModels" v-model="cardReaderModel" :enabled="false" />
            </div>
            <div>
              <Label :enabled="cardReaderEnabled">{{ $t('settings.select_connection') }}</Label>
              <DropDown :placeholder="$t('settings.select_connection')" :options="connectionOptions" v-model="cardReaderConnection" :enabled="false" />
            </div>
            <div v-if="cardReaderConnection == 'serial'">
              <Label :enabled="cardReaderEnabled">{{ $t('settings.serial_port') }}</Label>
              <DropDown :placeholder="$t('settings.select_port')" :options="availableSerialPorts" v-model="cardReaderSerialPort" :enabled="false" />
            </div>
            <div v-if="cardReaderConnection == 'tcp'">
              <Label :enabled="cardReaderEnabled">{{ $t('settings.ip_address') }}</Label>
              <TextBox placeholder="" v-model="cardReaderIPAddress" :enabled="false" :validation="ip(cardReaderIPAddress)" class="w-full" />
            </div>
            <div v-if="cardReaderConnection == 'tcp'">
              <Label :enabled="cardReaderEnabled">{{ $t('settings.port') }}</Label>
              <TextBox placeholder="" v-model="cardReaderPort" :enabled="false" :validation="port(cardReaderPort)" class="w-full" />
            </div>
          </div>

          <!-- Components: weight scale -->
          <div class="flex flex-col gap-4 w-full mt-0 mb-6" v-if="tab == 'components'">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{$t('settings.weightScaleSettings')}}</span>
            </h6>
            <div class="form-group flex flex-row gap-2">
              <CheckBox v-model="weightScaleEnabled" :enabled="false" :label="$t('settings.enable')" />
            </div>
            <div>
              <Label :enabled="weightScaleEnabled">{{ $t('settings.select_model') }}</Label>
              <DropDown :placeholder="$t('settings.select_model')" :options="weightScaleModels" v-model="weightScaleModel" :enabled="false" />
            </div>
            <div>
              <Label :enabled="weightScaleEnabled">{{ $t('settings.serial_port') }}</Label>
              <DropDown :placeholder="$t('settings.select_port')" :options="availableSerialPorts" v-model="weightScaleSerialPort" :enabled="false" />
            </div>
          </div>
          
        </div>
      </form>

    </div>
  </div>
</template>

<style scoped>
.screen-container {
  @apply block w-full p-5 dark:bg-gray-900 dark:text-white;
}
</style>
